import { useState } from "react";
import {
  Save,
  Bell,
  Key,
  Globe,
  Shield,
  Database,
  Webhook,
  Eye,
  EyeOff,
  Copy,
  Check,
} from "lucide-react";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { Dropdown } from "../components/Dropdown";

type SettingsTab =
  | "general"
  | "organization"
  | "security"
  | "integrations"
  | "notifications"
  | "api";

export function Settings() {
  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const [saved, setSaved] = useState(false);

  // API Key generation modal
  const [showGenKey, setShowGenKey] = useState(false);
  const [keyForm, setKeyForm] = useState({ name: "", scopes: [] as string[] });
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [keyVisible, setKeyVisible] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);

  const allScopes = [
    "read:all",
    "write:all",
    "write:evidence",
    "write:events",
    "write:findings",
    "admin",
  ];

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleGenerateKey = () => {
    const key = `ak_${Math.random().toString(36).slice(2, 10)}_${Math.random().toString(36).slice(2, 18)}`;
    setGeneratedKey(key);
  };

  const handleCopyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    }
  };

  const tabs: { id: SettingsTab; label: string; icon: React.ElementType }[] = [
    { id: "general", label: "General", icon: Globe },
    { id: "organization", label: "Organization", icon: Shield },
    { id: "security", label: "Security", icon: Key },
    { id: "integrations", label: "Integrations", icon: Webhook },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "api", label: "API Keys", icon: Database },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1>Settings</h1>
        <p className="text-sm d mt-0.5">
          Manage platform configuration and preferences
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar nav - horizontal tabs on mobile, vertical on md+ */}
        <div className="md:w-44 flex-shrink-0">
          <nav className="flex md:flex-col gap-1 overflow-x-auto pb-1 md:pb-0">
            {tabs.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-left transition-colors whitespace-nowrap md:w-full flex-shrink-0 ${
                  activeTab === id
                    ? "bg-foreground text-background"
                    : "d hover:bg-accent hover:text-foreground"
                }`}
              >
                <Icon className="size-4 flex-shrink-0" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-6">
          {activeTab === "general" && (
            <SettingsSection title="General Settings">
              <div className="space-y-4">
                <Field
                  label="Platform Name"
                  defaultValue="GRC Platform (OSCAL Native)"
                />
                <Field
                  label="Default Timezone"
                  defaultValue="UTC-5 (Eastern Time)"
                  type="select"
                  options={[
                    "UTC",
                    "UTC-5 (Eastern Time)",
                    "UTC-6 (Central Time)",
                    "UTC-7 (Mountain Time)",
                    "UTC-8 (Pacific Time)",
                  ]}
                />
                <Field
                  label="Date Format"
                  defaultValue="YYYY-MM-DD"
                  type="select"
                  options={["YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY"]}
                />
                <Field
                  label="OSCAL Version"
                  defaultValue="1.1.2"
                  type="select"
                  options={["1.0.4", "1.1.2", "1.1.3-dev"]}
                />
                <div className="pt-2">
                  <Toggle
                    label="Maintenance Mode"
                    description="Disable writes and show maintenance banner"
                    defaultChecked={false}
                  />
                </div>
              </div>
            </SettingsSection>
          )}

          {activeTab === "organization" && (
            <SettingsSection title="Organization">
              <div className="space-y-4">
                <Field
                  label="Organization Name"
                  defaultValue="Department of Example"
                />
                <Field label="Organization Identifier" defaultValue="deptex" />
                <Field
                  label="Federal Agency"
                  defaultValue="Department of Example (DEPTEX)"
                />
                <Field
                  label="FISMA System Inventory"
                  defaultValue="https://inventory.deptex.gov"
                />
                <Field
                  label="Authorizing Official"
                  defaultValue="Dr. Christine Moore"
                />
                <Field
                  label="Organization Email Domain"
                  defaultValue="@org.gov"
                />
              </div>
            </SettingsSection>
          )}

          {activeTab === "security" && (
            <div className="space-y-4">
              <SettingsSection title="Authentication">
                <div className="space-y-4">
                  <Toggle
                    label="Enforce MFA for All Users"
                    description="Require multi-factor authentication for every user"
                    defaultChecked={true}
                  />
                  <Toggle
                    label="SSO via SAML 2.0"
                    description="Enable single sign-on with your identity provider"
                    defaultChecked={true}
                  />
                  <Field
                    label="SSO Provider"
                    defaultValue="Azure Active Directory"
                    type="select"
                    options={[
                      "Azure Active Directory",
                      "Okta",
                      "GSA Login.gov",
                      "Custom SAML",
                    ]}
                  />
                  <Field label="Session Timeout (minutes)" defaultValue="60" />
                </div>
              </SettingsSection>
              <SettingsSection title="Password Policy">
                <div className="space-y-4">
                  <Field label="Minimum Length" defaultValue="12" />
                  <Toggle
                    label="Require Uppercase"
                    description="At least one character"
                    defaultChecked={true}
                  />
                  <Toggle
                    label="Require Special Characters"
                    description="At least one special character"
                    defaultChecked={true}
                  />
                  <Field
                    label="Password History"
                    defaultValue="12 previous passwords"
                  />
                  <Field label="Maximum Age (days)" defaultValue="90" />
                </div>
              </SettingsSection>
            </div>
          )}

          {activeTab === "integrations" && (
            <div className="space-y-3">
              {[
                {
                  name: "AWS Config",
                  description: "Configuration compliance monitoring",
                  status: true,
                  type: "Cloud",
                },
                {
                  name: "AWS CloudTrail",
                  description: "API activity audit logs",
                  status: true,
                  type: "Cloud",
                },
                {
                  name: "Azure Active Directory",
                  description: "Identity and access management",
                  status: true,
                  type: "Identity",
                },
                {
                  name: "CrowdStrike Falcon",
                  description: "Endpoint detection and response",
                  status: true,
                  type: "Security",
                },
                {
                  name: "Splunk SIEM",
                  description: "Security event aggregation",
                  status: true,
                  type: "SIEM",
                },
                {
                  name: "HashiCorp Vault",
                  description: "Secrets management",
                  status: true,
                  type: "Security",
                },
                {
                  name: "Tenable.io",
                  description: "Vulnerability scanning",
                  status: false,
                  type: "Scanner",
                },
                {
                  name: "Jira",
                  description: "Ticket management for POA&Ms",
                  status: false,
                  type: "Ticketing",
                },
              ].map((int) => (
                <div
                  key={int.name}
                  className="rounded-lg border border-border bg-card p-4 flex items-center gap-4 flex-wrap"
                >
                  <div className="size-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <Webhook className="size-4 d" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{int.name}</p>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted d">
                        {int.type}
                      </span>
                    </div>
                    <p className="text-xs d mt-0.5">{int.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div
                      className={`size-2 rounded-full ${int.status ? "bg-emerald-500" : "bg-muted-foreground"}`}
                    />
                    <span className="text-xs d">
                      {int.status ? "Connected" : "Not connected"}
                    </span>
                    <button className="text-xs px-2.5 py-1 rounded-md border border-border hover:bg-accent transition-colors">
                      {int.status ? "Configure" : "Connect"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "notifications" && (
            <SettingsSection title="Notification Preferences">
              <div className="space-y-4">
                {[
                  {
                    label: "New Drift Alert",
                    description: "Notify when configuration drift is detected",
                  },
                  {
                    label: "Finding Created",
                    description: "Notify when a new finding is created",
                  },
                  {
                    label: "POAM Due Date Approaching",
                    description: "Notify 7 days before a POAM is due",
                  },
                  {
                    label: "POAM Overdue",
                    description: "Notify when a POAM passes its due date",
                  },
                  {
                    label: "Workflow Approval Required",
                    description: "Notify when an item needs your approval",
                  },
                  {
                    label: "Assessment Completed",
                    description: "Notify when an assessment is finalized",
                  },
                  {
                    label: "SSP Change Request",
                    description: "Notify when an SSP change is submitted",
                  },
                  {
                    label: "Weekly Compliance Report",
                    description: "Receive weekly compliance summary email",
                  },
                ].map((notif) => (
                  <Toggle
                    key={notif.label}
                    label={notif.label}
                    description={notif.description}
                    defaultChecked={true}
                  />
                ))}
              </div>
            </SettingsSection>
          )}

          {activeTab === "api" && (
            <SettingsSection title="API Keys">
              <div className="space-y-4">
                <p className="text-sm d">
                  API keys provide programmatic access to the GRC Platform.
                  Treat them like passwords.
                </p>
                <div className="space-y-2">
                  {[
                    {
                      name: "CI/CD Pipeline Key",
                      created: "2026-01-15",
                      lastUsed: "5 min ago",
                      scopes: ["read:all", "write:evidence"],
                    },
                    {
                      name: "Reporting Key",
                      created: "2025-11-01",
                      lastUsed: "2026-03-07",
                      scopes: ["read:all"],
                    },
                    {
                      name: "Integration Key - Splunk",
                      created: "2025-10-15",
                      lastUsed: "2 min ago",
                      scopes: ["write:events"],
                    },
                  ].map((key) => (
                    <div
                      key={key.name}
                      className="rounded-md border border-border p-3 flex items-start gap-3 flex-wrap"
                    >
                      <Key className="size-4 d mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{key.name}</p>
                        <p className="text-xs d mt-0.5">
                          Created: {key.created} · Last used: {key.lastUsed}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {key.scopes.map((scope) => (
                            <span
                              key={scope}
                              className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted d"
                            >
                              {scope}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button className="text-xs px-2.5 py-1 rounded-md border border-border hover:bg-accent transition-colors text-red-500 hover:text-red-600">
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setShowGenKey(true);
                    setGeneratedKey(null);
                    setKeyForm({ name: "", scopes: [] });
                  }}
                  className="flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-border hover:bg-accent transition-colors"
                >
                  <Key className="size-4" />
                  Generate New API Key
                </button>
              </div>
            </SettingsSection>
          )}

          {/* Save button */}
          {activeTab !== "api" && activeTab !== "integrations" && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
              >
                <Save className="size-4" />
                {saved ? "Saved!" : "Save Changes"}
              </button>
              <button className="px-4 py-2 rounded-md border border-border text-sm hover:bg-accent transition-colors">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Generate API Key Modal */}
      <Modal
        open={showGenKey}
        onClose={() => {
          setShowGenKey(false);
          setGeneratedKey(null);
        }}
        title="Generate API Key"
        description="Create a new API key for programmatic access"
        size="md"
        footer={
          generatedKey ? (
            <button
              onClick={() => {
                setShowGenKey(false);
                setGeneratedKey(null);
              }}
              className="px-4 py-2 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Done
            </button>
          ) : (
            <ModalFooterButtons
              onCancel={() => setShowGenKey(false)}
              onSubmit={handleGenerateKey}
              submitLabel="Generate Key"
              submitDisabled={!keyForm.name || keyForm.scopes.length === 0}
            />
          )
        }
      >
        {!generatedKey ? (
          <>
            <FormField
              label="Key Name"
              required
              hint="A descriptive name to identify this key's purpose"
            >
              <FormInput
                placeholder="e.g. Production CI/CD Pipeline"
                value={keyForm.name}
                onChange={(v) => setKeyForm((f) => ({ ...f, name: v }))}
              />
            </FormField>
            <FormField
              label="Scopes"
              required
              hint="Select the permissions this key should have"
            >
              <div className="grid grid-cols-2 gap-2 mt-1">
                {allScopes.map((scope) => (
                  <label
                    key={scope}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={keyForm.scopes.includes(scope)}
                      onChange={(e) => {
                        setKeyForm((f) => ({
                          ...f,
                          scopes: e.target.checked
                            ? [...f.scopes, scope]
                            : f.scopes.filter((s) => s !== scope),
                        }));
                      }}
                      className="accent-foreground"
                    />
                    <span className="text-xs font-mono d">{scope}</span>
                  </label>
                ))}
              </div>
            </FormField>
          </>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-md bg-emerald-500/10 border border-emerald-500/20">
              <Check className="size-4 text-emerald-500 flex-shrink-0" />
              <p className="text-sm text-emerald-700 dark:text-emerald-400">
                API key generated successfully. Copy it now - it won't be shown
                again.
              </p>
            </div>
            <div>
              <p className="text-xs d mb-2">Your new API key:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs font-mono bg-muted/50 border border-border rounded-md px-3 py-2 break-all">
                  {keyVisible ? generatedKey : "•".repeat(generatedKey.length)}
                </code>
                <button
                  onClick={() => setKeyVisible((v) => !v)}
                  className="p-2 rounded-md border border-border hover:bg-accent transition-colors flex-shrink-0"
                >
                  {keyVisible ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
                <button
                  onClick={handleCopyKey}
                  className="p-2 rounded-md border border-border hover:bg-accent transition-colors flex-shrink-0"
                >
                  {keyCopied ? (
                    <Check className="size-4 text-emerald-500" />
                  ) : (
                    <Copy className="size-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {keyForm.scopes.map((scope) => (
                <span
                  key={scope}
                  className="text-xs font-mono px-1.5 py-0.5 rounded bg-muted d"
                >
                  {scope}
                </span>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function SettingsSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-5 space-y-4">
      <h3 className="text-sm font-medium">{title}</h3>
      {children}
    </div>
  );
}

function Field({
  label,
  defaultValue,
  type = "text",
  options,
}: {
  label: string;
  defaultValue: string;
  type?: "text" | "select";
  options?: string[];
}) {
  const [value, setValue] = useState(defaultValue);
  return (
    <div>
      <label className="text-xs d block mb-1">{label}</label>
      {type === "select" ? (
        <Dropdown
          variant="form"
          value={value}
          onChange={(v) => setValue(v)}
          options={(options ?? []).map((opt) => ({ value: opt, label: opt }))}
        />
      ) : (
        <input
          type="text"
          defaultValue={defaultValue}
          className="w-full text-sm bg-muted/50 rounded px-3 py-2 outline-none border border-border focus:border-foreground transition-colors"
        />
      )}
    </div>
  );
}

function Toggle({
  label,
  description,
  defaultChecked,
}: {
  label: string;
  description: string;
  defaultChecked: boolean;
}) {
  const [checked, setChecked] = useState(defaultChecked);
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <div className="flex-shrink-0 mt-0.5 relative">
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          onChange={() => setChecked(!checked)}
        />
        <div
          onClick={() => setChecked(!checked)}
          className={`w-9 h-5 rounded-full transition-colors ${checked ? "bg-foreground" : "bg-muted"}`}
        >
          <div
            className={`absolute top-0.5 size-4 rounded-full bg-background shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`}
          />
        </div>
      </div>
      <div>
        <p className="text-sm">{label}</p>
        <p className="text-xs d mt-0.5">{description}</p>
      </div>
    </label>
  );
}
